import {Reminder} from '../../../../../../backend/interfaces/reminder';
import {ReminderOrigin} from '../../../../../../backend/enums/reminder-origin';
import {EntityUserRemindersRepository} from '../../../../../../repositories/entity-user-reminders';
import {RemindersRepository} from '../../../../../../repositories/reminders';
import {
  buildReminderPayload,
  ReminderFormValues,
} from '../../../informal-caregiver/reminder/reminder-form.utils';

export type GroupReminder = Reminder & {id: string};

// Exclude delivery state and recipient, but include every exposed schedule setting.
export function reminderGroupKey(r: Reminder): string {
  return JSON.stringify([
    // Generic POST does not accept an origin, so informal and legacy records
    // created through it have the same generic provenance.
    r.origin === ReminderOrigin.InformalCaregiver ? undefined : r.origin,
    r.entityId,
    r.fromUserId,
    r.title,
    r.message,
    r.isActive,
    r.responseType,
    new Date(r.startAt).toISOString(),
    r.recurrenceType,
    r.recurrenceCount,
    r.recurrenceSecondsOfTheMinute,
    r.recurrenceMinutesOfTheHour,
    r.recurrenceHoursOfTheDay,
    r.recurrenceDaysOfTheWeek,
    r.recurrenceDaysOfTheMonth,
    r.recurrenceMonthsOfTheYear,
    r.recurrenceAdvancedConfiguration,
    r.imageUrl,
    r.legacySystem,
    r.legacyCreatedAt,
  ]);
}

export function reminderPayload(
  values: ReminderFormValues,
  original: Reminder,
  recipientId: string
) {
  return {
    ...buildReminderPayload(values, original.fromUserId, recipientId, original),
    toUserId: recipientId,
    recurrenceSecondsOfTheMinute: original.recurrenceSecondsOfTheMinute,
    recurrenceMinutesOfTheHour: original.recurrenceMinutesOfTheHour,
    recurrenceHoursOfTheDay: original.recurrenceHoursOfTheDay,
    recurrenceDaysOfTheWeek: original.recurrenceDaysOfTheWeek,
    recurrenceDaysOfTheMonth: original.recurrenceDaysOfTheMonth,
    recurrenceMonthsOfTheYear: original.recurrenceMonthsOfTheYear,
    recurrenceAdvancedConfiguration: original.recurrenceAdvancedConfiguration,
    imageUrl: original.imageUrl,
    legacySystem: original.legacySystem,
    legacyCreatedAt: original.legacyCreatedAt,
  };
}

export function repositoryFor(reminder: Reminder) {
  return reminder.origin === ReminderOrigin.ProfessionalCaregiver && reminder.entityId
    ? new EntityUserRemindersRepository(reminder.entityId, reminder.toUserId)
    : RemindersRepository.instance;
}

export type ReminderOperation = {run: () => Promise<unknown>};

export function editOperations(
  records: GroupReminder[],
  values: ReminderFormValues,
  removed: string[],
  added: string[],
  entityId: string
): ReminderOperation[] {
  const operations: ReminderOperation[] = records.map((r) => ({
    run: () =>
      removed.includes(r.toUserId)
        ? repositoryFor(r).deleteById(r.id)
        : repositoryFor(r).updateById(r.id, reminderPayload(values, r, r.toUserId)),
  }));
  for (const id of added) {
    const original = records[0];
    operations.push({
      run: () =>
        new EntityUserRemindersRepository(entityId, id).create(
          reminderPayload(values, original, id)
        ),
    });
  }
  return operations;
}
